import type {
  DeviceId,
  PropertySchema,
  PropertyValues,
  QuarterTurn,
  SignalKind,
  Size,
  TerminalId,
  Transform2D,
  Vec2,
} from '@/core/types';
import type { TranslationKey } from '@/i18n';

/** Every device kind the platform will eventually support (ROADMAP phases 8–13). */
export const DEVICE_TYPES = [
  'button',
  'switch',
  'emergency-stop',
  'sensor',
  'jam-sensor',
  'level-sensor',
  'lamp',
  'motor',
  'buzzer',
  'fan',
  'conveyor',
  'cylinder',
  'valve',
  'tank',
  'pump',
  'mixer',
  'product',
  'pid-controller',
  'modbus-server',
  'modbus-client',
] as const;
export type DeviceType = (typeof DEVICE_TYPES)[number];

export const DEVICE_CATEGORIES = ['input', 'output', 'mechanical', 'process', 'network'] as const;
export type DeviceCategory = (typeof DEVICE_CATEGORIES)[number];

export type DevicePosition = Vec2;
export type DeviceRotation = QuarterTurn;

/**
 * Serializable device instance stored in the project document (ARCHITECTURE §5.8).
 * Runtime state (`DeviceState`) is deliberately NOT part of it (principle P6).
 */
export interface Device {
  readonly id: DeviceId;
  readonly type: DeviceType;
  readonly name: string;
  readonly transform: Transform2D;
  readonly props: PropertyValues;
}

/** Base of every device runtime state; concrete devices extend it (e.g. a motor state machine). */
export interface DeviceState {
  /** Set while an injected or detected fault keeps the device out of normal operation (Phase 20). */
  readonly faulted: boolean;
}

/** `device-to-plc`: the device drives a PLC input; `plc-to-device`: a PLC output drives the device. */
export type TerminalDirection = 'device-to-plc' | 'plc-to-device';

export interface TerminalSpec {
  readonly id: TerminalId;
  readonly kind: SignalKind;
  readonly direction: TerminalDirection;
  readonly labelKey: TranslationKey;
}

/**
 * Whether the device can already be placed in a scene.
 * Phase 1 registers every planned device as `'planned'` so the toolbox can list it; the phase
 * that implements a device flips it to `'available'`.
 */
export type DeviceAvailability = 'planned' | 'available';

/**
 * Registry entry of a device kind (ARCHITECTURE §5.6).
 * Phase 1 only holds the static metadata. `behavior`, `renderer` and `createState` are added
 * by the phase that implements the device (Phase 8+) without touching existing fields.
 */
export interface DeviceDefinition {
  readonly type: DeviceType;
  readonly category: DeviceCategory;
  readonly availability: DeviceAvailability;
  readonly meta: {
    readonly nameKey: TranslationKey;
    readonly descriptionKey: TranslationKey;
    /** Icon name resolved by the UI icon registry; devices never import UI code. */
    readonly icon: string;
  };
  readonly size: Size;
  readonly defaultProps: PropertyValues;
  readonly propertySchema: PropertySchema;
  readonly terminals: readonly TerminalSpec[];
}
